import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    onAuthStateChanged,
    signOut,
    signInWithEmailAndPassword
} from 'firebase/auth';
import {
    collection,
    doc,
    getDoc,
    setDoc,
    getDocs,
    query,
    orderBy,
    Timestamp,
    where
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../../firebase';
import PosterHeader from '../../components/header/PosterHeaderBar/PosterHeader';
import Footer from '../../components/footer/footer';
import createPlaceholderPosts from '../../utils/createPlaceholderPosts';
import './BlogPage.css';

function BlogPage() {
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const navigate = useNavigate();

    // Check authentication state and admin status
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);

            if (currentUser) {
                // Check if the user is an admin
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (userDoc.exists() && userDoc.data().isAdmin) {
                    setIsAdmin(true);
                } else {
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
            }
        });

        // Fetch blog posts
        fetchBlogPosts();

        return () => unsubscribe();
    }, []);

    // Fetch all blog posts ordered by date (newest first)
    const fetchBlogPosts = async () => {
        try {
            const postsQuery = query(
                collection(db, 'blogPosts'),
                orderBy('date', 'desc')
            );
            const querySnapshot = await getDocs(postsQuery);

            // Get the current date at midnight for comparison
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const posts = [];
            querySnapshot.forEach((doc) => {
                const post = doc.data();
                post.id = doc.id;

                // Convert Firestore timestamp to JS Date
                if (post.date && post.date.toDate) {
                    post.date = post.date.toDate();
                }

                posts.push(post);
            });

            setBlogPosts(posts);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching blog posts:', error);
            setLoading(false);
        }
    };

    // Handle navigation to create new post
    const handleNewPost = () => {
        navigate('/blog/new');
    };

    // Generate placeholder posts
    const handleGeneratePlaceholders = async () => {
        if (!isAdmin) return;

        try {
            setGenerating(true);
            await createPlaceholderPosts(14, 7); // 14 days past, 7 days future
            await fetchBlogPosts(); // Refresh posts after generating
            setGenerating(false);
            alert('Placeholder posts generated successfully!');
        } catch (error) {
            console.error('Error generating placeholder posts:', error);
            setGenerating(false);
            alert('Error generating placeholder posts.');
        }
    };

    // Sign out
    const handleSignOut = async () => {
        try {
            await signOut(auth);
            navigate('/blog');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    // Format date for display
    const formatDate = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Check if a date is today
    const isToday = (date) => {
        if (!date) return false;
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    // Check if a post is empty (for applying styling)
    const isEmptyPost = (post) => {
        return !post.title && !post.content && !post.imageUrl;
    };

    // Navigate to specific blog post
    const navigateToPost = (postId) => {
        navigate(`/blog/post/${postId}`);
    };

    // Navigate to admin login
    const navigateToAdmin = () => {
        navigate('/blog/admin');
    };

    return (
        <div className="blog-page">
            <PosterHeader />

            <div className="blog-container">
                <h1 className="blog-title">Habby's Blog</h1>

                {/* Admin controls */}
                {isAdmin && (
                    <div className="admin-controls">
                        <button onClick={handleNewPost} className="new-post-btn">New Post</button>
                        <button onClick={handleGeneratePlaceholders} className="gen-placeholders-btn" disabled={generating}>
                            {generating ? 'Generating...' : 'Generate Placeholders'}
                        </button>
                        <button onClick={handleSignOut} className="signout-btn">Sign Out</button>
                    </div>
                )}

                {/* Hidden link for admin access */}
                {!user && (
                    <div className="admin-link">
                        <button onClick={navigateToAdmin} className="hidden-admin-link">·</button>
                    </div>
                )}

                {/* Blog posts list */}
                {loading ? (
                    <div className="loading">Loading blog posts...</div>
                ) : (
                    <div className="blog-posts">
                        {blogPosts.length === 0 ? (
                            <div className="no-posts">No blog posts yet.</div>
                        ) : (
                            blogPosts.map((post) => (
                                <div
                                    key={post.id}
                                    onClick={() => navigateToPost(post.id)}
                                    className={`blog-post ${isEmptyPost(post) ? 'empty-post' : ''} ${isToday(post.date) ? 'today-post' : ''}`}
                                >
                                    <div className="post-date"
                                        style={{
                                            color: isEmptyPost(post) ? 'red' : 'inherit',
                                            fontSize: isEmptyPost(post) ? '0.8rem' : 'inherit'
                                        }}>
                                        {formatDate(post.date)}
                                        {isToday(post.date) && <span className="today-badge">Today</span>}
                                    </div>

                                    {!isEmptyPost(post) && (
                                        <>
                                            {post.imageUrl && (
                                                <div className="post-image">
                                                    <img src={post.imageUrl} alt={post.title || 'Blog post'} />
                                                </div>
                                            )}

                                            {post.title && <h2 className="post-title">{post.title}</h2>}

                                            {post.content && (
                                                <div className="post-excerpt">
                                                    {post.content.substring(0, 150)}
                                                    {post.content.length > 150 ? '...' : ''}
                                                </div>
                                            )}

                                            <div className="read-more">Read more →</div>
                                        </>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
}

export default BlogPage; 